/**
 * The label of each template setting, in the words the template editors use for its control, keyed
 * by the setting's dot path in the saved configuration. Passed as `labels` to `flattenSettings` /
 * `diffSettings` so a list of changed settings names them as the admin knows them.
 *
 * Covers both template kinds: the Intune deployment template (DeploymentTemplateWizard, its
 * AdvancedSettings step) and the user experience template (UxTemplateWizard). A path missing here
 * falls back to the generic label `flattenSettings` builds from the key.
 */
export const TEMPLATE_SETTING_LABELS: Record<string, string> = {
  // Intune deployment template: Metadata step.
  'intuneMetadata.owner': 'Owner',
  'intuneMetadata.developer': 'Developer',
  'intuneMetadata.notes': 'Notes',
  'intuneMetadata.categories': 'Category',
  'intuneMetadata.featured': 'Featured app',
  'intuneMetadata.informationUrl': 'Information URL',
  'intuneMetadata.privacyUrl': 'Privacy URL',
  allowUninstallFromCompanyPortal: 'Allow available uninstallation in Company Portal',
  'naming.appPattern': 'Application name',
  'naming.updateAppPattern': 'Update-only application name',
  'naming.groupPattern': 'Group name',
  'naming.updateGroupPattern': 'Update-only group name',
  // Advanced step.
  'requirements.architectures': 'Operating system architecture',
  'requirements.minimumOs': 'Minimum operating system',
  'requirements.diskSpaceMb': 'Disk space required (MB)',
  'requirements.memoryMb': 'Physical memory required (MB)',
  'requirements.logicalProcessors': 'Minimum number of logical processors',
  'requirements.cpuSpeedMhz': 'Minimum CPU speed required (MHz)',
  'requirements.rules': 'Additional requirement rules',
  restartBehaviour: 'Device restart behaviour',
  returnCodes: 'Return codes',
  // Install and Update-only app steps.
  skipInstall: 'Skip install',
  appAssignments: 'App assignments',
  'updateOnly.enabled': 'Deploy an update-only app',
  'updateOnly.matchUnmanaged': 'Which installs does it patch?',
  'updateOnly.assignments': 'Update-only app assignments',
  // Automated updates step.
  'automatedUpdates.followNewVersions': 'Follow new versions',
  'automatedUpdates.soakDays': 'Wait before publishing',
  'automatedUpdates.keepVersions': 'Versions to keep in Intune',
  'automatedUpdates.supersede': 'Supersede the previous version',
  'automatedUpdates.uninstallPrevious': 'Previous version',
  'automatedUpdates.autoUpdateFromCompanyPortal':
    'Auto-update apps installed from the Company Portal',

  // User experience template: appearance.
  psadtVersion: 'PSADT version',
  'identity.companyName': 'Company name',
  'identity.dialogStyle': 'Dialog style',
  'identity.logoSource': 'Logo and banner',
  'identity.fluentAccent': 'Accent colour',
  'identity.languageOverride': 'Dialog language',
  'identity.balloonNotifications': 'Show balloon notifications',
  'postInstall.removeDesktopShortcuts': 'Remove desktop shortcuts',
  // Install experience.
  'closeApps.mode': 'When the app is already running',
  'closeApps.countdownSeconds': 'Countdown before closing',
  'closeApps.forceCountdown': 'Show the countdown right away',
  'defer.allow': 'Let the user postpone the install',
  'defer.count': 'How many times',
  'defer.onTimeout': 'If they do nothing',
  'timers.notifyTimeoutMinutes': 'Wait for a response',
  'welcome.persistPrompt': 'Make them respond',
  'dialogs.showProgress': 'Show install progress',
  'dialogs.interactive': 'Interactive',
  // Uninstall experience: the same controls, so each is named with its section.
  'uninstall.mode': 'When the app is still running',
  'uninstall.countdownSeconds': 'Uninstall › Countdown before closing',
  'uninstall.forceCountdown': 'Uninstall › Show the countdown right away',
  'uninstall.allowDefer': 'Let the user postpone the uninstall',
  'uninstall.deferCount': 'Uninstall › How many times',
  'uninstall.onTimeout': 'Uninstall › If they do nothing',
  'uninstall.notifyTimeoutMinutes': 'Uninstall › Wait for a response',
  'uninstall.persistPrompt': 'Uninstall › Make them respond',
  'uninstall.showProgress': 'Show uninstall progress',
  'uninstall.interactive': 'Uninstall › Interactive',
}
